/** @jsxImportSource ../i18n */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Briefcase, Check, ChevronDown, Eye, Newspaper, Plus, RefreshCw, Sparkles, X } from 'lucide-react';
import { fetchPortfolio, fetchWatchlist, addToWatchlist, removeFromWatchlist, fetchStockRumors, fetchBatchRumors, fetchRumorsThread } from '../store/slices/portfolioSlice';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import api from '../services/api';
import { isEtf } from '../components/Watchlist';
import { BrokerGate } from '../components/shell/Broker';
import { MarkdownProse } from '../components/markdown/MarkdownProse';
import { cx } from '../theme/cx';
import { formatTime } from '../theme/format';
import {
  Badge, Banner, Button, Card, CardHeader, EmptyState, IconButton, PageHeader, SearchInput, SkeletonText, Spinner, SymbolMark, Tabs,
} from '../components/ui';

const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`;

// Search of European stocks and ETFs to add to the shared watchlist, with its
// results in a popover under the field.
const WatchlistManager = ({ watchlist, onAdd, onRemove, pending, error }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = query.trim();
    setResults([]);
    if (term.length < 2) {
      setSearching(false);
      return undefined;
    }
    let active = true;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/market/search/eu?q=${encodeURIComponent(term)}`);
        if (active) setResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (active) setResults([]);
      } finally {
        if (active) setSearching(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query]);

  const add = async (item) => {
    if (await onAdd(item)) setQuery('');
  };

  return (
    <Card>
      <CardHeader icon={Eye} title="Gérer la watchlist" description="Ajoutez des actions ou des ETF européens pour suivre leurs rumeurs ; la liste est partagée avec le portefeuille." />
      <div className="relative">
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onClear={() => setQuery('')}
          placeholder="Rechercher une action ou un ETF : ASML, TotalEnergies, LQQ…"
          aria-label="Rechercher une valeur à ajouter à la watchlist"
        />
        {results.length > 0 && (
          <ul className="absolute inset-x-0 top-full z-20 mt-2 max-h-72 divide-y divide-line overflow-y-auto rounded-control border border-line-strong bg-surface-3 shadow-pop" aria-label="Résultats de recherche">
            {results.map((r, i) => {
              const sym = r.Symbol || r.symbol;
              const desc = r.Description || r.description;
              const already = watchlist.some(w => w.symbol === sym);
              return (
                <li key={`${sym}-${i}`}>
                  <button
                    type="button"
                    onClick={() => !already && add(r)}
                    disabled={already || pending}
                    className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-surface-2 disabled:cursor-default disabled:opacity-60"
                  >
                    <SymbolMark symbol={sym} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2 text-sm font-medium text-fg">
                        {sym}
                        {isEtf(r) && <Badge tone="gold">ETF</Badge>}
                      </span>
                      <span className="block truncate text-xs text-fg-subtle">{desc}</span>
                    </span>
                    {already
                      ? <span className="flex items-center gap-1 text-xs text-fg-subtle"><Check size={13} aria-hidden="true" />Déjà ajouté</span>
                      : <Plus size={16} aria-hidden="true" className="flex-shrink-0 text-gold-400" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {searching && <Spinner size={14} label="Recherche en cours…" className="mt-3" />}
      {error && <Banner tone="loss" compact role="alert" className="mt-3">{error}</Banner>}
      {watchlist.length > 0 ? (
        <ul className="mt-4 flex flex-wrap gap-2" aria-label="Titres surveillés">
          {watchlist.map((item) => (
            <li key={item.id || item.symbol} className="flex items-center gap-2 rounded-control border border-line bg-surface-2 py-1 pl-1 pr-1.5">
              <SymbolMark symbol={item.symbol} size="sm" />
              <span className="text-sm font-medium text-fg">{item.symbol}</span>
              {item.description && <span className="hidden max-w-[10rem] truncate text-xs text-fg-subtle sm:inline">{item.description}</span>}
              <button
                type="button"
                aria-label={`Retirer ${item.symbol}`}
                title={`Retirer ${item.symbol}`}
                disabled={pending}
                onClick={() => onRemove(item.symbol)}
                className="inline-flex h-6 w-6 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-surface-3 hover:text-loss-300 disabled:opacity-40"
              >
                <X size={13} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-sm text-fg-muted">Votre watchlist est vide. Ajoutez des actions ou des ETF pour suivre leurs rumeurs.</p>
      )}
    </Card>
  );
};

const Rumors = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { positions, saxoConnected, watchlist, stockRumors, stockRumorsLoading, stockRumorsErrors, rumorsThread, rumorsThreadLoading, rumorsThreadError } = useSelector((state) => state.portfolio);
  const { language } = useLanguage();

  const [activeTab, setActiveTab] = useState('portfolio');
  const [autoFetched, setAutoFetched] = useState({ portfolio: false, watchlist: false });
  const [threadOpen, setThreadOpen] = useState(true);
  const [watchlistPending, setWatchlistPending] = useState(false);
  const [watchlistError, setWatchlistError] = useState('');

  useEffect(() => {
    if (saxoConnected) {
      dispatch(fetchPortfolio());
      dispatch(fetchWatchlist());
    }
  }, [dispatch, saxoConnected]);

  // Stable between renders: a fresh array would re-run the auto-fetch below on
  // every render and loop (the batch dispatch re-renders synchronously before
  // the autoFetched flag is applied).
  const currentStocks = useMemo(
    () => (activeTab === 'portfolio' ? positions : watchlist.map(w => ({ symbol: w.symbol, description: w.description }))),
    [activeTab, positions, watchlist],
  );

  // Auto-fetch all rumors in parallel via batch endpoint
  useEffect(() => {
    if (currentStocks.length === 0 || autoFetched[activeTab]) return;
    const uncached = currentStocks.filter(s => !stockRumors[`${s.symbol}_${language}`]);
    if (uncached.length === 0) return;
    setAutoFetched(prev => ({ ...prev, [activeTab]: true }));
    dispatch(fetchBatchRumors({ stocks: uncached.map(s => ({ symbol: s.symbol, description: s.description })), language }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStocks, activeTab, autoFetched, dispatch, language]);

  const handleAddToWatchlist = async (item) => {
    const symbol = item.Symbol || item.symbol;
    setWatchlistPending(true);
    setWatchlistError('');
    try {
      await dispatch(addToWatchlist({ symbol, description: item.Description || item.description })).unwrap();
      toast.success(`${symbol} ajouté à la watchlist`);
      return true;
    } catch (error) {
      setWatchlistError(typeof error === 'string' ? error : "Impossible d'ajouter ce titre.");
      return false;
    } finally {
      setWatchlistPending(false);
    }
  };

  const handleRemoveFromWatchlist = async (symbol) => {
    setWatchlistPending(true);
    setWatchlistError('');
    try {
      await dispatch(removeFromWatchlist(symbol)).unwrap();
      toast.success(`${symbol} retiré de la watchlist`);
    } catch (error) {
      setWatchlistError(typeof error === 'string' ? error : 'Impossible de retirer ce titre.');
    } finally {
      setWatchlistPending(false);
    }
  };

  const fetchRumors = (symbol, description, forceRefresh = false) => {
    dispatch(fetchStockRumors({ symbol, description, language, forceRefresh }));
  };

  const handleRegenerateAll = () => {
    dispatch(fetchBatchRumors({ stocks: currentStocks.map(s => ({ symbol: s.symbol, description: s.description })), language, forceRefresh: true }));
  };

  const handleGenerateThread = (forceRefresh = false) => {
    const stocks = currentStocks.map(s => ({ symbol: s.symbol, description: s.description }));
    dispatch(fetchRumorsThread({ stocks, language, forceRefresh }));
  };

  const loadingCount = Object.keys(stockRumorsLoading).length;
  const anyLoading = loadingCount > 0;
  const totalCount = currentStocks.length;
  const cachedCount = currentStocks.filter(s => stockRumors[`${s.symbol}_${language}`]).length;

  const tabs = [
    { id: 'portfolio', label: 'Portefeuille', icon: Briefcase, tabId: 'rumors-tab-portfolio', panelId: 'rumors-panel' },
    { id: 'watchlist', label: 'Watchlist', count: watchlist.length, icon: Eye, tabId: 'rumors-tab-watchlist', panelId: 'rumors-panel' },
  ];

  const threadSubtitle = rumorsThread
    ? `Synthèse de ${plural(rumorsThread.stock_count, 'valeur', 'valeurs')} — ${formatTime(rumorsThread.generated_at)}`
    : 'Un briefing unique à partir des rumeurs de chaque valeur.';

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Intelligence"
        title="Rumeurs de marché"
        description="Signaux faibles et bruits de marché sur vos valeurs, à recouper avant toute décision."
        meta={saxoConnected && totalCount > 0 && (
          anyLoading
            ? <Badge tone="gold" dot="pulse">Génération… {plural(loadingCount, 'valeur restante', 'valeurs restantes')}</Badge>
            : <Badge>{cachedCount}/{totalCount} valeurs chargées — en cache pour aujourd'hui</Badge>
        )}
        actions={saxoConnected && (
          <Button variant="secondary" leadingIcon={RefreshCw} loading={anyLoading} disabled={currentStocks.length === 0} onClick={handleRegenerateAll}>
            Tout régénérer
          </Button>
        )}
      />
      <BrokerGate>
        <div className="space-y-6">
          <Tabs ariaLabel="Valeurs suivies" items={tabs} value={activeTab} onChange={setActiveTab} />

          <div role="tabpanel" id="rumors-panel" aria-labelledby={`rumors-tab-${activeTab}`} className="space-y-6">
            <Card padding="none" className="overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 sm:px-6">
                <button
                  type="button"
                  onClick={() => setThreadOpen(!threadOpen)}
                  aria-expanded={threadOpen}
                  aria-controls="rumors-thread"
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                  <span aria-hidden="true" className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-control border border-line bg-surface-2 text-gold-400"><Newspaper size={17} /></span>
                  <span className="min-w-0">
                    <span className="block text-[17px] font-semibold text-fg">Synthèse du jour</span>
                    <span className="block truncate text-sm text-fg-muted">{threadSubtitle}</span>
                  </span>
                  <ChevronDown size={18} aria-hidden="true" className={cx('ml-auto flex-shrink-0 text-fg-subtle transition-transform duration-200', !threadOpen && '-rotate-90')} />
                </button>
                {rumorsThread && !rumorsThreadLoading && (
                  <IconButton icon={RefreshCw} label="Régénérer la synthèse" variant="secondary" size="sm" onClick={() => handleGenerateThread(true)} />
                )}
              </div>
              {threadOpen && (
                <div id="rumors-thread" className="border-t border-line px-5 py-5 sm:px-6">
                  {rumorsThreadLoading && (
                    <div role="status" className="space-y-4">
                      <Spinner size={16} label={`Génération de la synthèse à partir de ${plural(cachedCount, 'rumeur', 'rumeurs')}…`} />
                      <SkeletonText lines={5} />
                    </div>
                  )}
                  {rumorsThreadError && !rumorsThreadLoading && (
                    <Banner
                      tone="loss"
                      role="alert"
                      action={cachedCount > 0 ? <Button variant="link" onClick={() => handleGenerateThread()}>Réessayer</Button> : null}
                    >
                      {rumorsThreadError}
                    </Banner>
                  )}
                  {!rumorsThread && !rumorsThreadLoading && !rumorsThreadError && (
                    <EmptyState
                      icon={Sparkles}
                      description={cachedCount === 0
                        ? 'En attente des rumeurs de chaque valeur avant de générer la synthèse…'
                        : `${plural(cachedCount, 'rumeur prête', 'rumeurs prêtes')} : générez la synthèse du jour.`}
                      action={cachedCount > 0 ? <Button leadingIcon={Sparkles} onClick={() => handleGenerateThread()}>Générer la synthèse</Button> : null}
                    />
                  )}
                  {rumorsThread && !rumorsThreadLoading && <MarkdownProse variant="rumor">{rumorsThread.content}</MarkdownProse>}
                </div>
              )}
            </Card>

            {activeTab === 'watchlist' && (
              <WatchlistManager
                watchlist={watchlist}
                onAdd={handleAddToWatchlist}
                onRemove={handleRemoveFromWatchlist}
                pending={watchlistPending}
                error={watchlistError}
              />
            )}

            {currentStocks.length === 0 ? (
              <Card>
                <EmptyState
                  icon={activeTab === 'portfolio' ? Briefcase : Eye}
                  description={activeTab === 'portfolio' ? 'Aucune position trouvée.' : 'Votre watchlist est vide. Ajoutez des valeurs pour voir leurs rumeurs.'}
                />
              </Card>
            ) : (
              <div className="space-y-4">
                {currentStocks.map((stock) => {
                  const sym = stock.symbol;
                  const desc = stock.description;
                  const rumors = stockRumors[`${sym}_${language}`];
                  const isLoading = !!stockRumorsLoading[sym];
                  const stockError = stockRumorsErrors?.[sym];
                  return (
                    <Card key={sym} padding="none" className="overflow-hidden">
                      <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3.5 sm:px-6">
                        <Link to={`/stock/${encodeURIComponent(sym)}`} className="group flex min-w-0 items-center gap-3">
                          <SymbolMark symbol={sym} />
                          <span className="min-w-0">
                            <span className="block font-medium text-fg transition-colors group-hover:text-gold-200">{sym}</span>
                            <span className="block truncate text-xs text-fg-subtle">{desc}</span>
                          </span>
                        </Link>
                        <IconButton
                          icon={RefreshCw}
                          label={`Actualiser les rumeurs de ${sym}`}
                          variant="secondary"
                          size="sm"
                          disabled={isLoading}
                          iconClassName={isLoading ? 'animate-spin' : undefined}
                          onClick={() => fetchRumors(sym, desc, true)}
                        />
                      </div>
                      <div className="px-5 py-5 sm:px-6">
                        {isLoading && (
                          <div role="status" className="space-y-4">
                            <Spinner size={16} label={`Recherche des rumeurs pour ${sym}…`} />
                            <SkeletonText lines={4} />
                          </div>
                        )}
                        {!isLoading && !rumors && stockError && (
                          <Banner tone="loss" role="alert" action={<Button variant="link" onClick={() => fetchRumors(sym, desc, true)}>Réessayer</Button>}>
                            {stockError}
                          </Banner>
                        )}
                        {!isLoading && !rumors && !stockError && <Spinner size={14} label="En file d'attente…" />}
                        {rumors && <MarkdownProse variant="rumor">{rumors.content}</MarkdownProse>}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </BrokerGate>
    </div>
  );
};

export default Rumors;
